import type { BladeAction, BladeStackState } from './types';

/** The empty stack: no blades, explorer mode. */
export const initialBladeStack: BladeStackState = {
  blades: [],
  activeIndex: -1,
  mode: 'explorer',
};

function clampActive(len: number, idx: number): number {
  if (len === 0) return -1;
  if (idx < 0) return 0;
  if (idx > len - 1) return len - 1;
  return idx;
}

/**
 * bladeStackReducer is the single source of truth for the blade stack (HUB-275).
 * Pure: same input → same output, no side effects. Simple-path traversal — opening
 * a child truncates the path to its right (you cannot fork the stack).
 */
export function bladeStackReducer(state: BladeStackState, action: BladeAction): BladeStackState {
  switch (action.type) {
    case 'open': {
      // Keep blades[0..at] (inclusive), drop everything to the right, then push.
      // `at` of -1 starts a fresh root; `at` beyond the end appends.
      const keep = Math.max(0, Math.min(action.at + 1, state.blades.length));
      const blades = [...state.blades.slice(0, keep), action.blade];
      return { ...state, blades, activeIndex: blades.length - 1 };
    }
    case 'close': {
      if (action.index < 0 || action.index >= state.blades.length) return state;
      const blades = state.blades.slice(0, action.index);
      return { ...state, blades, activeIndex: blades.length - 1 };
    }
    case 'activate': {
      if (action.index < 0 || action.index >= state.blades.length) return state;
      return { ...state, activeIndex: action.index };
    }
    case 'setMode':
      if (action.mode === state.mode) return state;
      return { ...state, mode: action.mode };
    case 'hydrate': {
      // Normalize the active index against the hydrated blades (URLs can be edited).
      const { blades, mode } = action.state;
      return { blades, mode, activeIndex: clampActive(blades.length, action.state.activeIndex) };
    }
    default:
      return state;
  }
}
